import React, { useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { useSearchParams } from "react-router-dom";
import {
  Users,
  TrashIcon,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Papa from "papaparse";
import UsermanMenu from "../components/UsermanMenu";

const UsermanUserProfile = () => {
  const [searchParams] = useSearchParams();
  const usermanId = searchParams.get("userman_id");
  const deviceName = searchParams.get("device_name");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [form, setForm] = useState({ username: "", profile: "" });
  const [filter, setFilter] = useState({
    username: "",
    profile: "",
    state: "",
  });
  const token = localStorage.getItem("token");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/userman-user-profile-get/${usermanId}`, {
        headers: { authorization: token },
      });
      setData(res.data.data || []);
    } catch (err) {
      console.error("Gagal fetch user-profile:", err);
    } finally {
      setLoading(false);
    }
  }, [usermanId, token]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/userman-user-get/${usermanId}`, {
        headers: { authorization: token },
      });
      setUsers(res.data.data || []);
    } catch (err) {
      console.error("Gagal fetch users:", err);
    } finally {
      setLoading(false);
    }
  }, [usermanId, token]);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/userman-profile-get/${usermanId}`, {
        headers: { authorization: token },
      });
      setProfiles(res.data.data || []);
    } catch (err) {
      console.error("Gagal fetch profiles:", err);
    } finally {
      setLoading(false);
    }
  }, [usermanId, token]);

  const applyFilters = useCallback(() => {
    let result = [...data];
    if (filter.username) {
      result = result.filter((item) =>
        item.user.toLowerCase().includes(filter.username.toLowerCase())
      );
    }
    if (filter.profile) {
      result = result.filter((item) =>
        item.profile.toLowerCase().includes(filter.profile.toLowerCase())
      );
    }
    if (filter.state) {
      result = result.filter((item) =>
        item.state.toLowerCase().includes(filter.state.toLowerCase())
      );
    }
    setFilteredData(result);
  }, [data, filter]);

  useEffect(() => {
    fetchData();
    fetchUsers();
    fetchProfiles();
  }, [fetchData, fetchUsers, fetchProfiles]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    if (!form.username || !form.profile) return;

    try {
      await api.post(
        `/userman-user-profile-add`,
        {
          userman_id: usermanId,
          newUserProfile: form,
        },
        {
          headers: { authorization: token },
        }
      );
      setForm({ username: "", profile: "" });
      fetchData();
    } catch (err) {
      console.error("Gagal tambah user-profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (username) => {
    setLoading(true);
    if (!window.confirm(`Yakin ingin menghapus user-profile '${username}'?`))
      return;

    try {
      await api.delete(`/userman-user-profile-delete`, {
        headers: { authorization: token },
        data: { userman_id: usermanId, username },
      });
      fetchData();
    } catch (err) {
      console.error("Gagal hapus user-profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const csv = Papa.unparse(
      filteredData.map(({ user, profile, state, "end-time": endTime }) => ({
        Username: user,
        Profile: profile,
        Status: state,
        "End Time": endTime,
      }))
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "userman_user_profiles.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-16 h-16 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users /> {deviceName} /
          <UsermanMenu userman_id={usermanId} device_name={deviceName} />/ User
          Profile
        </h1>
        <button
          onClick={exportToCSV}
          className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-md"
        >
          <Download size={16} /> Export CSV
        </button>
      </div>

      {/* Form Tambah */}
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
      >
        <select
          className="border px-4 py-2 text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })}
          required
        >
          <option value="">-- Pilih Username --</option>
          {users.map((u) => (
            <option key={u.name} value={u.name}>
              {u.name}
            </option>
          ))}
        </select>
        <select
          className="border px-4 py-2 text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
          value={form.profile}
          onChange={(e) => setForm({ ...form, profile: e.target.value })}
          required
        >
          <option value="">-- Pilih Profile --</option>
          {profiles.map((p) => (
            <option key={p.name} value={p.name}>
              {p.name}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Tambah
        </button>
      </form>

      {/* Filter */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <input
          type="text"
          placeholder="Filter Username"
          value={filter.username}
          onChange={(e) => setFilter({ ...filter, username: e.target.value })}
          className="px-3 py-2 text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
        />
        <input
          type="text"
          placeholder="Filter Profile"
          value={filter.profile}
          onChange={(e) => setFilter({ ...filter, profile: e.target.value })}
          className="px-3 py-2 text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
        />
        <input
          type="text"
          placeholder="Filter Status"
          value={filter.state}
          onChange={(e) => setFilter({ ...filter, state: e.target.value })}
          className="px-3 py-2 text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl shadow bg-white dark:bg-gray-800">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="p-3 text-left font-semibold">User</th>
              <th className="p-3 text-left font-semibold">Profile</th>
              <th className="p-3 text-left font-semibold">State</th>
              <th className="p-3 text-left font-semibold">End-Time</th>
              <th className="p-3 text-left font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {/* Hapus kondisi loading di sini */}
            {currentData.length > 0 ? (
              currentData.map((item) => (
                <tr key={item[".id"]} className="border-t dark:border-gray-700">
                  <td className="p-3">{item.user}</td>
                  <td className="p-3">{item.profile}</td>
                  <td className="p-3 capitalize">{item.state}</td>
                  <td className="p-3">{item["end-time"]}</td>
                  <td className="p-3 flex justify-center gap-3">
                    <button
                      onClick={() => handleDelete(item.user)}
                      className="flex items-center gap-1 px-3 py-2 text-red-600 hover:text-red-800 rounded-md border border-red-600 hover:border-red-800 text-xs"
                    >
                      <TrashIcon size={16} /> Hapus
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-4 text-gray-500">
                  Tidak ada data.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex justify-between items-center p-4">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`flex items-center gap-1 px-3 py-1 rounded border ${
                currentPage === 1
                  ? "text-gray-400 border-gray-300 cursor-not-allowed"
                  : "text-black dark:text-white border-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              <ChevronLeft size={16} /> Prev
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Halaman {currentPage} dari {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`flex items-center gap-1 px-3 py-1 rounded border ${
                currentPage === totalPages
                  ? "text-gray-400 border-gray-300 cursor-not-allowed"
                  : "text-black dark:text-white border-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsermanUserProfile;
